import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { Button, ConfirmationModal, Paper } from '@insio/ui-toolkit';
import { GetProfileDocument, LeaveManagingMasterAccountDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useMasterAccountBoardErrorMessage } from '@/hooks/use-master-account-board-error';
import { isDetachNameTakenError } from '../account-relations/map-account-relation-error';

interface LeaveMasterAccountSectionProps {
  /** Name of the master account currently managing the caller's own account. */
  masterAccountName: string;
}

/**
 * Lets a managed client's ADMIN disconnect from its master account. Irreversible: the
 * account is assigned the default plan and starts paying for itself again.
 */
export function LeaveMasterAccountSection({ masterAccountName }: LeaveMasterAccountSectionProps) {
  const t = useFeatureTranslation('systemSettings.organization');
  const { showSuccess, showError } = useToastHelpers();
  const masterAccountBoardError = useMasterAccountBoardErrorMessage();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [leaveManagingMasterAccount, { loading }] = useMutation(
    LeaveManagingMasterAccountDocument,
    {
      refetchQueries: [{ query: GetProfileDocument }],
      awaitRefetchQueries: true
    }
  );

  const handleConfirm = async () => {
    try {
      const { error } = await leaveManagingMasterAccount();
      // errorPolicy 'all' resolves a GraphQL refusal instead of throwing it.
      if (error) throw error;
      showSuccess(t('leaveMasterAccount.success'));
      setConfirmOpen(false);
    } catch (e) {
      showError(
        masterAccountBoardError(e) ??
          (isDetachNameTakenError(e)
            ? t('leaveMasterAccount.errorDetachNameTaken')
            : t('leaveMasterAccount.error'))
      );
      setConfirmOpen(false);
    }
  };

  return (
    <Paper showDivider={false} padding='none'>
      <Paper.Content className='p-md'>
        <div className='flex flex-wrap items-center justify-between gap-md'>
          <div className='space-y-2xs'>
            <p className='font-medium text-foreground-base'>{t('leaveMasterAccount.title')}</p>
            <p className='text-sm text-foreground-muted'>
              {t('leaveMasterAccount.description', { masterAccountName })}
            </p>
          </div>
          <Button outline='error' onClick={() => setConfirmOpen(true)}>
            {t('leaveMasterAccount.button')}
          </Button>
        </div>
      </Paper.Content>

      <ConfirmationModal
        open={confirmOpen}
        variant='danger'
        title={t('leaveMasterAccount.confirmTitle')}
        message={t('leaveMasterAccount.confirmMessage', { masterAccountName })}
        warning={t('leaveMasterAccount.confirmWarning')}
        confirmLabel={t('leaveMasterAccount.confirmButton')}
        cancelLabel={t('leaveMasterAccount.cancelButton')}
        loading={loading}
        onConfirm={() => void handleConfirm()}
        onClose={() => setConfirmOpen(false)}
      />
    </Paper>
  );
}
